import React from "react";
import { Modal } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export interface ChartDataset {
  legend: string;
  values: number[];
  color: string;
}

export interface ChartInfo {
  title: string;
  labels: string[];
  datasets: ChartDataset[];
}

interface ChartModalProps {
  show: boolean;
  onHide: () => void;
  data: ChartInfo | null;
}

const ChartModal: React.FC<ChartModalProps> = ({ show, onHide, data }) => {
  if (!data) return null;

  // Transform to recharts format: [{name: "Label1", Legend1: 10, Legend2: 20}, ...]
  const chartData = data.labels.map((label, idx) => {
    const point: Record<string, any> = { name: label };
    data.datasets.forEach((ds) => {
      point[ds.legend] = ds.values[idx] ?? 0;
    });
    return point;
  });

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header>
        <Modal.Title style={{ fontSize: "1rem" }}>{data.title}</Modal.Title>
        <button
          type="button"
          onClick={onHide}
          aria-label="Close"
          style={{
            background: "transparent",
            border: "none",
            fontSize: "1.4rem",
            lineHeight: 1,
            cursor: "pointer",
            padding: "4px 8px",
            color: "var(--text-strong)",
            opacity: 0.5,
          }}
        >
          &times;
        </button>
      </Modal.Header>
      <Modal.Body>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 10, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-35}
              textAnchor="end"
              interval={0}
              fontSize={11}
              height={80}
            />
            <YAxis fontSize={11} tickFormatter={(v) => v.toLocaleString()} />
            <Tooltip
              formatter={(value: number) => value.toLocaleString()}
            />
            {data.datasets.length > 1 && <Legend />}
            {data.datasets.map((ds) => (
              <Bar
                key={ds.legend}
                dataKey={ds.legend}
                fill={ds.color}
                radius={[3, 3, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Modal.Body>
    </Modal>
  );
};

export default ChartModal;
